import { Divider, MenuItem, Stack, Theme, Typography } from '@mui/material'
import { findIndex, nth } from 'lodash'
import { SyntheticEvent } from 'react'
import { FiPlus } from 'react-icons/fi'
import { TbBoxMultiple } from 'react-icons/tb'
import useStyles from '~/common/hooks/useStyles'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DetailedTableTab } from '~/components/tables/types'
import { Menu } from '~/components/ui/atoms/menu/Menu'
import { Tooltip } from '~/components/ui/atoms/tooltip/Tooltip'
interface TableTabMenuProps {
  handleDropdownClose: (event: Event | SyntheticEvent) => void
  handleAddNewTableTabClick: (event: React.MouseEvent) => void
  getTableTabNameDisplay: (tableTab: DetailedTableTab) => string
  tabCountLimit: number
  textLengthLimit: number
  orderedTableTabs: DetailedTableTab[]
  setOrderedTableTabs: (tableTabs: DetailedTableTab[]) => void
  handleTabChange: (tableTab: DetailedTableTab) => void
  handleTabMenuClose: () => void
  open: boolean
  anchorEl: HTMLElement | null
  readOnly: boolean
  canConfigureTableTabs: boolean
}

function getStyles(theme: Theme) {
  return {
    paperContainer: {
      marginTop: theme.spacing(0.5),
    },
    menuItem: {
      width: '100%',
    },
    divider: {
      '&.MuiDivider-root': {
        margin: 0,
      },
    },
  }
}

/**
 * Renders menu to select table tabs if table has more tabs than are visible at the top of the
 * table. Also includes a button to create a new tab on the table
 */
export function SelectTableTabMenu({
  handleDropdownClose,
  handleAddNewTableTabClick,
  getTableTabNameDisplay,
  tabCountLimit,
  textLengthLimit,
  orderedTableTabs,
  setOrderedTableTabs,
  handleTabChange,
  handleTabMenuClose,
  open,
  anchorEl,
  readOnly,
  canConfigureTableTabs,
}: TableTabMenuProps) {
  const styles = useStyles(getStyles)
  const { t } = useTypedTranslation()
  const moveTabToVisibleTabs = (fromIndex: number) => {
    const copyOrderedTableTabs = [...orderedTableTabs]
    const tab = nth(copyOrderedTableTabs, fromIndex)
    if (tab) {
      // Remove tab from current spot
      copyOrderedTableTabs.splice(fromIndex, 1)
      // Insert tab to last visible tab spot
      copyOrderedTableTabs.splice(tabCountLimit - 1, 0, tab)
      setOrderedTableTabs(copyOrderedTableTabs)
    }
  }

  const handleAdditionalTabSelection = (
    event: React.MouseEvent<HTMLElement>,
    selectedTab: DetailedTableTab,
  ) => {
    // On selection of table tab from dropdown, replace the last table tab from header display
    // with the newly selected table tab and place that table tab into the dropdown
    event.stopPropagation()
    const selectedTabIdIndex = findIndex(orderedTableTabs, (tab) => tab.id === selectedTab.id)
    moveTabToVisibleTabs(selectedTabIdIndex)
    handleTabChange(selectedTab)
    handleDropdownClose(event)
    handleTabMenuClose()
  }

  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 35,
        horizontal: 'left',
      }}
      onClose={handleDropdownClose}
      open={open}
    >
      {orderedTableTabs.slice(tabCountLimit).map((tableTab) => (
        <MenuItem key={tableTab.id}>
          <Tooltip
            key={tableTab.id}
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 10],
                  },
                },
              ],
            }}
            arrow
            placement="right"
            title={open && tableTab.name.length > textLengthLimit ? tableTab.name : ''}
          >
            <Stack
              alignItems="center"
              direction="row"
              display="flex"
              justifyContent="flex-start"
              onClick={(event) => handleAdditionalTabSelection(event, tableTab)}
              spacing={1}
              sx={styles.menuItem}
            >
              <TbBoxMultiple size={12} />
              <Typography variant="body1">{getTableTabNameDisplay(tableTab)}</Typography>
            </Stack>
          </Tooltip>
        </MenuItem>
      ))}
      <Divider sx={styles.divider} />
      {!readOnly && canConfigureTableTabs && (
        <MenuItem
          onClick={(event) => {
            handleDropdownClose(event)
            handleAddNewTableTabClick(event)
          }}
        >
          <Stack
            alignItems="center"
            direction="row"
            display="flex"
            justifyContent="flex-start"
            spacing={1}
          >
            <FiPlus size={12} />
            <Typography variant="body1">{t('add_new_button.add_new')}</Typography>
          </Stack>
        </MenuItem>
      )}
    </Menu>
  )
}
